import {
    AlphaAttribute,
    ColorAttribute,
    getAttributesToSerialize,
    NumberAttribute,
    StringAttribute,
    StringOrNumberAttribute,
} from '../../../attributes';
import type { TextDynamicBoxRendererSettings } from '../renderer/TextDynamicBoxRenderer';
import { TextDynamicBoxRenderer } from '../renderer/TextDynamicBoxRenderer';
import { Config } from '../../../../video-data/config';
import type { DynamicHeight, DynamicWidth } from '../../../../enums';
import {
    DynamicInferredHeight,
    DynamicInferredWidth,
    DynamicXDirection,
    DynamicYDirection,
} from '../../../../enums';
import { int2rgba } from '../../../../libs/videoData';

import { TextAssetDynamicGraphic } from './TextAssetDynamicGraphic';

export class TextAssetDynamicBox extends TextAssetDynamicGraphic {
    override _classType = 'CanvasTextDynamicBox';

    override readonly type = Config.BOX_GRAPHIC;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredWidth) })
    accessor boxWidth: DynamicWidth = DynamicInferredWidth.width;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredHeight) })
    accessor boxHeight: DynamicHeight = DynamicInferredHeight.maxHeight;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor topLeftRadius = 0;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor topRightRadius = 0;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor bottomLeftRadius = 0;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor bottomRightRadius = 0;
    @NumberAttribute()
    accessor paddingTop = 0;
    @NumberAttribute()
    accessor paddingRight = 0;
    @NumberAttribute()
    accessor paddingBottom = 0;
    @NumberAttribute()
    accessor paddingLeft = 0;
    @StringAttribute({ choices: Object.values(DynamicXDirection) })
    accessor xDirection = DynamicXDirection.right;
    @StringAttribute({ choices: Object.values(DynamicYDirection) })
    accessor yDirection = DynamicYDirection.bottom;
    @NumberAttribute()
    accessor xProgress = 1.0;
    @NumberAttribute()
    accessor yProgress = 1.0;
    @ColorAttribute()
    accessor strokeColor = 0xffffff;
    @AlphaAttribute()
    accessor strokeAlpha = 1;
    @NumberAttribute({ min: 0, animatable: false })
    accessor strokeWidth = 0;
    @ColorAttribute()
    accessor fill = 0xff00ff;
    @AlphaAttribute()
    accessor fillAlpha = 0.5;

    override getCanvasRendererSettings(): TextDynamicBoxRendererSettings {
        const base = super.getCanvasRendererSettings();

        const boxWidth =
            typeof this.boxWidth === 'number'
                ? this.boxWidth * this.referenceToOutputMultiplier
                : this.boxWidth;
        const boxHeight =
            typeof this.boxHeight === 'number'
                ? this.boxHeight * this.referenceToOutputMultiplier
                : this.boxHeight;
        const strokeStyle = int2rgba(this.strokeColor, this.strokeAlpha);

        return {
            ...base,
            type: this.type,
            boxWidth,
            boxHeight,
            topLeftRadius: this.topLeftRadius * this.referenceToOutputMultiplier,
            topRightRadius: this.topRightRadius * this.referenceToOutputMultiplier,
            bottomLeftRadius: this.bottomLeftRadius * this.referenceToOutputMultiplier,
            bottomRightRadius: this.bottomRightRadius * this.referenceToOutputMultiplier,
            paddingTop: this.paddingTop * this.referenceToOutputMultiplier,
            paddingRight: this.paddingRight * this.referenceToOutputMultiplier,
            paddingBottom: this.paddingBottom * this.referenceToOutputMultiplier,
            paddingLeft: this.paddingLeft * this.referenceToOutputMultiplier,
            strokeStyle,
            strokeWidth: this.strokeWidth * this.referenceToOutputMultiplier,
            xDirection: this.xDirection,
            yDirection: this.yDirection,
            xProgress: this.xProgress,
            yProgress: this.yProgress,
            fill: int2rgba(this.fill, this.fillAlpha),
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(getAttributesToSerialize(this));
    }

    /* The list of properties that can be animated during draw */
    override get animatableProperties() {
        // Since TextDynamicBox is responsible for rendering our text we need to ask it which properties it
        // supports. See its rendering logic for more details.
        return TextDynamicBoxRenderer.animatableProperties;
    }

    override get editableProperties() {
        return super.editableProperties.concat(['fill', 'fillAlpha', 'strokeColor', 'strokeAlpha']);
    }

    override get animatableGeometricProperties() {
        return TextDynamicBoxRenderer.animatableGeometricProperties;
    }
}
